/**
 * 應用專案路由定義
 *
 * 在此檔案新增應用專案的路由，基底平台的 App.tsx 會自動載入。
 * 不需要修改 App.tsx。
 *
 * 使用方式：
 * 1. 建立頁面元件（如 src/pages/ApInvItemsPage.tsx）
 * 2. 在下方 import 並加入 Route
 *
 * 範例：
 * import ApInvItemsPage from '../pages/ApInvItemsPage';
 *
 * 然後在 appRoutes 陣列中新增：
 * { path: 'ap_inv_items', element: <ApInvItemsPage /> },
 */

import React from 'react';
import ResearchTopicsPage from '../pages/ResearchTopicsPage';
import ResearchTopicDetailPage from '../pages/ResearchTopicDetailPage';
import ResearchSubjectsPage from '../pages/ResearchSubjectsPage';
import ResearchSubjectDetailPage from '../pages/ResearchSubjectDetailPage';
import OpsCentersPage from '../pages/OpsCentersPage';
import OpsCenterDetailPage from '../pages/OpsCenterDetailPage';
import OpsCenterPresentPage from '../pages/OpsCenterPresentPage';
import ClearanceBrokerProfilePage from '../pages/ClearanceBrokerProfilePage';
import ClearanceCustomersPage from '../pages/ClearanceCustomersPage';
import ClearanceCasesPage from '../pages/ClearanceCasesPage';
import ClearanceCaseDetailPage from '../pages/ClearanceCaseDetailPage';
import ClearanceTrainingCenterPage from '../pages/ClearanceTrainingCenterPage';
import ClearanceDocAnnotationPage from '../pages/ClearanceDocAnnotationPage';
import EEMailServerPage from '../pages/EEMailServerPage';
import EEMailDomainsPage from '../pages/EEMailDomainsPage';
import EEMailMyMailboxPage from '../pages/EEMailMyMailboxPage';
import EEMailAccountsPage from '../pages/EEMailAccountsPage';
import LifeCenterPage from '../pages/LifeCenterPage';
import MyCitiesPage from '../pages/MyCitiesPage';
import TripPlannerPage from '../pages/TripPlannerPage';
import HealthPage from '../pages/HealthPage';
import EeMyLocationsPage from '../pages/EeMyLocationsPage';
import EeMyContactsPage from '../pages/EeMyContactsPage';
import EeMyCalendarPage from '../pages/EeMyCalendarPage';
import DwgTestPage from '../pages/DwgTestPage';
import TenantSettingsPage from '../pages/TenantSettingsPage';
import SystemOfficeManagementPage from '../pages/SystemOfficeManagementPage';
import SystemSubscriptionDashboardPage from '../pages/SystemSubscriptionDashboardPage';
import TenantSubscriptionPage from '../pages/TenantSubscriptionPage';
import FormManagerPage from '../pages/ee/formBuilder/FormManagerPage';
import FormBuilderPage from '../pages/ee/formBuilder/FormBuilderPage';
import FormReviewPage from '../pages/ee/formBuilder/FormReviewPage';
import FormGrantsPage from '../pages/ee/formBuilder/FormGrantsPage';
import FormWorkspacePage from '../pages/ee/formBuilder/FormWorkspacePage';
import FormFillPage from '../pages/ee/formBuilder/FormFillPage';
import FormViewPage from '../pages/ee/formBuilder/FormViewPage';
import OfficeMeetingsPage from '../pages/ee/officeMeeting/OfficeMeetingsPage';
import OfficeMeetingCreatePage from '../pages/ee/officeMeeting/OfficeMeetingCreatePage';
import OfficeMeetingDetailPage from '../pages/ee/officeMeeting/OfficeMeetingDetailPage';
import OfficeMeetingRoomPage from '../pages/ee/officeMeeting/OfficeMeetingRoomPage';
import OfficeMeetingMinutesPage from '../pages/ee/officeMeeting/OfficeMeetingMinutesPage';
import TenantAiConfigPage from '../pages/ee/aiChat/TenantAiConfigPage';
import EeHrResumePage from '../pages/ee/EeHrResumePage';
import EeMyResumePage from '../pages/EeMyResumePage';
import PlatformBillingPage from '../pages/ee/platformBilling/PlatformBillingPage';
import MyBillsPage from '../pages/ee/platformBilling/MyBillsPage';

interface AppRoute {
  path: string;
  element: React.ReactElement;
}

const appRoutes: AppRoute[] = [
  // ===== 應用專案路由（在此新增）=====
  // AI 議題工作區（EE 模組 — func_code 用 ee_research_* 對齊 DB）
  { path: 'ee_research_subjects', element: <ResearchSubjectsPage /> },
  { path: 'ee_research_subjects/:id', element: <ResearchSubjectDetailPage /> },
  { path: 'ee_research_topics', element: <ResearchTopicsPage /> },
  { path: 'ee_research_topics/:topicCode', element: <ResearchTopicDetailPage /> },
  // 營運中心（EE 模組 — 知識庫 + 展示間 + 簡報生產）
  { path: 'ee_ops_centers', element: <OpsCentersPage /> },
  { path: 'ee_ops_centers/:code', element: <OpsCenterDetailPage /> },
  { path: 'ee_ops_centers/:code/present', element: <OpsCenterPresentPage /> },
  // 報關中心（EE 模組）
  { path: 'ee_clearance_broker_profile', element: <ClearanceBrokerProfilePage /> },
  { path: 'ee_clearance_customers', element: <ClearanceCustomersPage /> },
  { path: 'ee_clearance_cases', element: <ClearanceCasesPage /> },
  { path: 'ee_clearance_cases/:id', element: <ClearanceCaseDetailPage /> },
  { path: 'ee_clearance_training', element: <ClearanceTrainingCenterPage /> },
  { path: 'ee_clearance_training/samples/:sampleId/annotate', element: <ClearanceDocAnnotationPage /> },
  // 生活中心（EE 模組）
  { path: 'ee_life_center',    element: <LifeCenterPage /> },
  { path: 'ee_my_cities',      element: <MyCitiesPage /> },
  { path: 'ee_trip_planner',   element: <TripPlannerPage /> },
  { path: 'ee_health',         element: <HealthPage /> },
  { path: 'ee_my_locations',   element: <EeMyLocationsPage /> },
  { path: 'ee_my_contacts',   element: <EeMyContactsPage /> },
  { path: 'ee_my_calendar',   element: <EeMyCalendarPage /> },
  // 室內設計服務中心（EE 模組 — Sprint 0：DWG 估價測試）
  { path: 'ee_inhouse_dwg_test', element: <DwgTestPage /> },
  // 租戶設定作業（基底平台升級版 — Task-tenant-provisioning）
  { path: 'tenant_settings', element: <TenantSettingsPage /> },
  // Office 服務集中管理（Task-33 3.2e · SYSTEM_ADMIN · path === func_code）
  { path: 'system_office_management', element: <SystemOfficeManagementPage /> },
  // 服務訂閱框架（Sprint 4-pre · path === func_code · 選單由 migration 078 seed 帶出）
  { path: 'system_subscription_management', element: <SystemSubscriptionDashboardPage /> },
  { path: 'ee_subscription', element: <TenantSubscriptionPage /> },
  // 網域郵箱（EE 模組）
  { path: 'ee_mail_server', element: <EEMailServerPage /> },
  { path: 'ee_mail_domains', element: <EEMailDomainsPage /> },
  { path: 'ee_mail_my_mailbox', element: <EEMailMyMailboxPage /> },
  { path: 'ee_mail_accounts', element: <EEMailAccountsPage /> },
  // 表單製作器（Sprint 5 · path === func_code · 選單由 migration 083 seed 帶出）
  { path: 'form_manager', element: <FormManagerPage /> },
  { path: 'form_manager/review', element: <FormReviewPage /> },
  { path: 'form_manager/builder/:id', element: <FormBuilderPage /> },
  { path: 'form_manager/:id/grants', element: <FormGrantsPage /> },
  { path: 'form_workspace', element: <FormWorkspacePage /> },
  { path: 'form_workspace/:id/fill', element: <FormFillPage /> },
  { path: 'form_workspace/:id/view', element: <FormViewPage /> },
  // 公司會議室（Sprint 5-c · 行政中心 · path === func_code · 選單由 migration 086 seed 帶出）
  { path: 'ee_office_meeting', element: <OfficeMeetingsPage /> },
  { path: 'ee_office_meeting/create', element: <OfficeMeetingCreatePage /> },
  { path: 'ee_office_meeting/:id', element: <OfficeMeetingDetailPage /> },
  { path: 'ee_office_meeting/:id/room', element: <OfficeMeetingRoomPage /> },
  { path: 'ee_office_meeting/:id/minutes', element: <OfficeMeetingMinutesPage /> },
  // 租戶 AI 設定（Sprint 5-d PR-5·租戶管理員·由 chat profile「設定」進入·MainLayout 內）
  { path: 'ee_tenant_ai_config', element: <TenantAiConfigPage /> },
  // 人事履歷（人事模組 v2 Phase 1·path === func_code·選單由 migration 111 seed 帶出）
  { path: 'ee_hr_resume', element: <EeHrResumePage /> },   // 行政中心·員工履歷
  { path: 'ee_my_resume', element: <EeMyResumePage /> },   // 個人化功能·我的履歷
  // 平台收費（platform_billing·選單由 migration 112 seed 帶出）
  { path: 'ee_platform_billing', element: <PlatformBillingPage /> },   // 平台管理員·帳單管理
  { path: 'ee_my_bills', element: <MyBillsPage /> },                   // 組織管理員·我的租戶帳單
  // AI 對話（Sprint 5-d Hotfix）路由已移至 App.tsx 專屬 AIChatLayout group（Claude-style 全螢幕·不套 MainLayout）
  // Sprint 5-b PWA 路由亦於 App.tsx 專屬 MobileLayout group（standalone 行動版·不套 MainLayout）
];

export default appRoutes;
